import React from 'react';
import { Check, X, Calendar } from 'lucide-react';
import { isGreekHoliday, calculateDaysBetween, getLeaveYear } from '../utils/vacationUtils';

const ApprovalsView = ({ 
  currentUser, 
  pendingRequests, 
  userDatabase,
  annualBalances = [],
  vacationRequests = [],
  holidays = [],
  onRequestDecision 
}) => {
  const managerLevel = currentUser.managerLevel || currentUser.manager_level || 1;
  const relevantRequests = pendingRequests.filter(req => {
  const requestUser = userDatabase.find(u => u.id === (req.user_id || req.userId));
  if (!requestUser) return false;

  if (managerLevel === 2) {
    const isDirectReport = requestUser.manager_id === currentUser.id;
    return (req.status === 'pending' && isDirectReport) || req.status === 'manager1_approved';
  }

  return req.status === 'pending' && requestUser.manager_id === currentUser.id;
});

  const getDayBreakdown = (start, end) => {
    let workdays = 0;
    let weekends = 0;
    let holidayCount = 0;
    let foundHolidays = [];

    let current = new Date(start);
    const last = new Date(end);

    while (current <= last) {
      const dayOfWeek = current.getDay();
      const holidayName = isGreekHoliday(current);

      if (holidayName) {
        holidayCount++;
        foundHolidays.push(holidayName);
      }
      else if (dayOfWeek === 0 || dayOfWeek === 6) {
        weekends++;
      } 
      else {
        workdays++;
      }
      current.setDate(current.getDate() + 1);
      }
      return { workdays, weekends, holidayCount, foundHolidays: [...new Set(foundHolidays)] };
    };

    const getUserBalance = (userId) => {
      const currentYear = getLeaveYear(new Date());
      const years = [currentYear];

      const getAllowance = (year) => {
        const pivoted = annualBalances.find(b => Number(b.userId || b.user_id) === Number(userId));
        if (pivoted) {
          const v = pivoted[`balance_${year}`];
          if (v !== undefined) return Number(v);
        }
        return 0;
      };

    const getVacationUsed = (year) => {
      return vacationRequests
        .filter(req => {
          const reqUserId = req.user_id || req.userId;
          const isGranted = req.manager_granted || req.managerGranted;
          if (isGranted) return false;
          const startDate = req.start_date || req.startDate;
          if (!startDate) return false;
          const parts = startDate.toString().split('T')[0].split('-').map(Number);
          const cycleYear = parts[1] - 1 < 3 ? parts[0] - 1 : parts[0];

          return Number(reqUserId) === Number(userId) && req.status === 'approved' && cycleYear === year;
        })
        .reduce((sum, req) =>
          sum + calculateDaysBetween(req.start_date || req.startDate, req.end_date || req.endDate, holidays), 0);
    };

    const getGranted = (year) => {
      return vacationRequests
        .filter(req => {
          const reqUserId = req.user_id || req.userId;
          const isGranted = req.manager_granted || req.managerGranted;
          const startDate = req.start_date || req.startDate;
          if (!startDate || !isGranted) return false;
          const parts = startDate.toString().split('T')[0].split('-').map(Number);
          const cycleYear = parts[1] - 1 < 3 ? parts[0] - 1 : parts[0];
          return Number(reqUserId) === Number(userId) && req.status === 'approved' && cycleYear === year;
        })
        .reduce((sum, req) =>
          sum + calculateDaysBetween(req.start_date || req.startDate, req.end_date || req.endDate, holidays), 0);
      };

    return years.map(year => {
      const allowed = getAllowance(year);
      const used = getVacationUsed(year);
      const granted = getGranted(year);
      const prevAllowed = getAllowance(year - 1);
      const prevUsed = getVacationUsed(year - 1);
      const prevGranted = getGranted(year - 1);
      const overflowFromPrev = prevAllowed > 0
        ? Math.max(0, prevUsed + prevGranted - prevAllowed)
        : 0;
      const remaining = Math.max(0, allowed - used - granted - overflowFromPrev);
      return { year, allowed, remaining };
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-jewel-text xs:text-sm">Εγκρίσεις Αδειών</h2>
        </div>
        <div className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-md">
          {relevantRequests.length} εκκρεμείς
        </div>
      </div>
      {relevantRequests.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-jewel-bg rounded-lg border-2 border-dashed border-gray-300 dark:border-jewel-border">
          <Check className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-jewel-text-muted" />
          <p className="text-gray-500 dark:text-jewel-text-muted">Όλες οι αιτήσεις έχουν αξιολογηθεί.</p>
        </div>
      ) : (
        relevantRequests.map(request => {
          const user = userDatabase.find(u => u.id === (request.user_id || request.userId));
          const startDate = new Date(request.start_date || request.startDate);
          const endDate = new Date(request.end_date || request.endDate);
          const breakdown = getDayBreakdown(startDate, endDate);

          return (
            <div key={request.id} className="bg-white dark:bg-jewel-bg border border-gray-200 dark:border-jewel-border rounded-xl p-4 shadow-sm">
              <div>
                <h3 className="font-bold text-gray-900 dark:text-jewel-text leading-none">{user?.name}</h3>
                  <p className="text-xs text-gray-500 dark:text-jewel-text-muted mt-1">Αίτημα για {breakdown.workdays} ημέρες</p>
                    {user && (() => {
                      const balance = getUserBalance(user.id);
                      return (
                        <div className="flex gap-3 mt-1.5">
                          {balance.map(({ year, allowed, remaining }) => (
                            allowed > 0 && (
                              <span key={year} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                remaining <= 0 ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 text-[14px]' :
                                remaining <= 5 ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400 text-[14px]' :
                                'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-[14px]'
                              }`}>
                                {remaining} {remaining === 1 ? 'Ημέρα Διαθέσιμη' : 'Ημέρες Διαθέσιμες'} για τρέχων έτος
                              </span>
                            )
                          ))}
                        </div>
                      );
                    })()}
              </div>
              <div className="mb-4 p-3 bg-gray-50 dark:bg-jewel-bg/50 rounded-lg border border-gray-100 dark:border-jewel-border">
                <p className="text-[10px] uppercase font-bold text-gray-400 dark:text-jewel-text-muted mb-1">Περίοδος</p>
                <p className="text-sm font-semibold text-gray-800 dark:text-jewel-text">
                  {startDate.toLocaleDateString('el-GR', { weekday: 'long' })} {startDate.toLocaleDateString('el-GR')}
                  <span className="mx-2 text-gray-400 dark:text-jewel-text-muted font-normal">έως</span>
                  {endDate.toLocaleDateString('el-GR', { weekday: 'long' })} {endDate.toLocaleDateString('el-GR')}
                </p>
              </div>
              <div className="mb-4 p-3 bg-blue-50 dark:bg-jewel-teal/20 border border-blue-100 dark:border-jewel-teal rounded-lg">
                <div className="flex items-center space-x-2 text-[10px] font-bold text-blue-500 dark:text-jewel-teal-tint uppercase mb-2">
                  <Calendar className="h-3 w-3" />
                  <span>Ανάλυση Ημερών</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-blue-900 dark:text-blue-200">
                  <span className="font-bold">{breakdown.workdays === 1 ? '1 Εργάσιμη' : `${breakdown.workdays} Εργάσιμες`}</span>
                  
                  
                  {breakdown.weekends > 0 && (
                    <>
                      <span className="text-blue-300 dark:text-blue-700">|</span>
                      <span>{breakdown.weekends} Σαββατοκύριακα</span>
                    </>
                  )}
                  {breakdown.holidayCount > 0 && (
                    <>
                      <span className="text-blue-300 dark:text-blue-700">|</span>
                      <span className="flex items-center text-orange-600 dark:text-orange-400 font-medium">
                        {breakdown.holidayCount} Αργία: {breakdown.foundHolidays.join(', ')}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {request.reason && (
                <div className="mb-4 p-3 bg-gray-50 dark:bg-jewel-bg/50 rounded-lg text-sm text-gray-600 dark:text-jewel-text italic">
                  "{request.reason}"
                </div>
              )}
              <div className="flex space-x-3">
                <button
                  onClick={() => onRequestDecision(request.id, 'approved', managerLevel)}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-lg flex items-center justify-center space-x-2 font-medium transition-all active:scale-95"
                >
                  <Check className="h-4 w-4" /> <span>Έγκριση</span>
                </button>
                <button
                  onClick={() => onRequestDecision(request.id, 'rejected', managerLevel)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg flex items-center justify-center space-x-2 font-medium transition-all active:scale-95"
                >
                  <X className="h-4 w-4" /> <span>Απόρριψη</span>
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default ApprovalsView;